// WASender WhatsApp Service
// Documentation: Check your WASender dashboard for the correct API endpoint
// Common endpoints: 
// - https://app.wasender.com/api/v1/send
// - https://api.wasender.io/v1/messages
// - Or your custom instance URL

const WASENDER_API_KEY = import.meta.env.VITE_WASENDER_API_KEY || '';
const WASENDER_DEVICE_ID = import.meta.env.VITE_WASENDER_DEVICE_ID || '';
const WASENDER_API_URL = import.meta.env.VITE_WASENDER_API_URL || 'https://app.wasender.com/api/v1/send';

/**
 * Send WhatsApp message using WASender API
 * @param {string} phoneNumber - Phone number with country code (e.g., "918250676762")
 * @param {string} message - Message text to send
 * @returns {Promise<Object>} Response from WASender API
 */
export const sendWhatsAppMessage = async (phoneNumber, message) => {
    if (!WASENDER_API_KEY || !WASENDER_DEVICE_ID) {
        console.error('WASender API credentials not configured');
        throw new Error('WASender credentials missing. Please configure VITE_WASENDER_API_KEY and VITE_WASENDER_DEVICE_ID in .env file');
    }

    // Ensure phone number is clean and properly formatted
    let cleanPhone = phoneNumber.toString().replace(/[\s\-+()]/g, '');

    // Ensure phone number starts with country code (91 for India)
    if (!cleanPhone.startsWith('91') && cleanPhone.length === 10) {
        cleanPhone = '91' + cleanPhone;
    }

    console.log('Sending to phone:', cleanPhone);
    console.log('Using API URL:', WASENDER_API_URL);

    const url = WASENDER_API_URL;

    // WASender API expects 'to' and 'text' fields
    const payload = {
        device: WASENDER_DEVICE_ID,
        to: cleanPhone,
        text: message
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${WASENDER_API_KEY}`
            },
            body: JSON.stringify(payload)
        });

        // Handle rate limiting
        if (response.status === 429) {
            console.error('Rate limit exceeded. Please wait before sending more messages.');
            return {
                success: false,
                error: 'Rate limit exceeded. Please wait a few minutes before trying again.'
            };
        }

        const data = await response.json();

        if (!response.ok) {
            console.error('WASender API Error Response:', {
                status: response.status,
                statusText: response.statusText,
                error: data,
                payload: payload
            });

            // Provide more helpful error messages based on WASender API responses
            let errorMessage = data.message || 'Failed to send message';
            if (response.status === 400) {
                errorMessage = 'Bad request. Check phone number format and message content.';
            } else if (response.status === 401) {
                errorMessage = 'Unauthorized. Check your WASender API credentials.';
            } else if (response.status === 403) {
                errorMessage = 'Forbidden. Your WASender device may not be authorized.';
            } else if (response.status === 429) {
                errorMessage = 'Rate limit exceeded. Please wait before sending more messages.';
            } else if (response.status === 404) {
                errorMessage = 'Device not found. Please check your DEVICE_ID.';
            }

            throw new Error(errorMessage);
        }

        console.log('✅ WhatsApp message sent successfully via WASender API');
        return {
            success: true,
            data: data
        };
    } catch (error) {
        console.error('WASender API Error:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Send order confirmation to customer
 * @param {Object} orderDetails - Order details object
 * @returns {Promise<Object>} Response from WASender API
 */
export const sendOrderConfirmation = async (orderDetails) => {
    const {
        customerPhone,
        customerName,
        productName,
        bagSize,
        quantity,
        pricePerBag,
        subtotal,
        deliveryCharge,
        total,
        address,
        landmark,
        paymentMethod,
        distance
    } = orderDetails;

    const message = `✅ *Order Confirmed - Grainly*

Dear ${customerName}, your order has been confirmed!

📦 *Order Details:*
🌾 Product: ${productName}
📦 Bag Size: ${bagSize}
📦 Quantity: ${quantity} ${quantity === 1 ? 'bag' : 'bags'}
💰 Price per bag: ₹${pricePerBag}
💵 Subtotal: ₹${subtotal}
🚚 Delivery Charge: ₹${deliveryCharge}
💵 *Total Amount: ₹${total}*

🏠 *Delivery Address:*
${address}
${landmark ? `Landmark: ${landmark}` : ''}

💳 *Payment Method:* ${paymentMethod}
📏 *Distance: ${distance ? distance.toFixed(1) + ' km' : 'N/A'}*

Your order will be delivered soon. Thank you for choosing us! 🙏

For any queries, please contact us.`;

    return await sendWhatsAppMessage(customerPhone, message);
};

/**
 * Send order notification to shop owner
 * @param {Object} orderDetails - Order details object
 * @returns {Promise<Object>} Response from WASender API
 */
export const sendOrderNotificationToShop = async (orderDetails) => {
    const {
        shopPhone,
        customerName,
        customerPhone,
        productName,
        bagSize,
        quantity,
        pricePerBag,
        subtotal,
        deliveryCharge,
        total,
        address,
        landmark,
        paymentMethod,
        distance,
        googleMapsLink
    } = orderDetails;

    const message = `🛒 *New Order Received!*

👤 *Customer Details:*
Name: ${customerName}
Phone: ${customerPhone}

📦 *Order Details:*
🌾 Product: ${productName}
📦 Bag Size: ${bagSize}
📦 Quantity: ${quantity} ${quantity === 1 ? 'bag' : 'bags'}
💰 Price per bag: ₹${pricePerBag}
💵 Subtotal: ₹${subtotal}
🚚 Delivery Charge: ₹${deliveryCharge}
💵 *Total Amount: ₹${total}*

🏠 *Delivery Address:*
${address}
${landmark ? `🏛️ Landmark: ${landmark}` : ''}

📍 *Location:* ${googleMapsLink}

💳 *Payment Method:* ${paymentMethod}
📏 *Distance: ${distance ? distance.toFixed(1) + ' km' : 'N/A'}*

Please process this order. 🚀`;

    return await sendWhatsAppMessage(shopPhone, message);
};

export default {
    sendWhatsAppMessage,
    sendOrderConfirmation,
    sendOrderNotificationToShop
};
